import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useNotify } from "../../../context/NotificationContext";
import {
  Card,
  Form,
  Input,
  Select,
  Button,
  Typography,
  Spin,
  Space,
  Row,
  Col,
  Badge,
} from "antd";
import {
  SaveOutlined,
  ReloadOutlined,
  ArrowLeftOutlined,
  FileTextOutlined,
  GlobalOutlined,
  PlusOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import masterService from "../../../services/masterService";
import { noWhitespaceValidator } from "../../../utils/commonFunctions";
import { useCommon } from "../../../context/CommonContext";

const { Title, Text } = Typography;
const { Option } = Select;

const EditContentType = () => {
  const { ct_id } = useParams();
  const navigate = useNavigate();
  const { setPageTitle } = useCommon();
  const notify = useNotify();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [languages, setLanguages] = useState([]);
  const [multilingualData, setMultilingualData] = useState([]);

  const fetchContentTypeDetails = useCallback(async () => {
    if (!ct_id) {
      notify.error({ message: "Content type ID is required" });
      navigate("/content-types");
      return;
    }

    setLoadingData(true);
    try {
      const response = await masterService.getSingleContentType(ct_id);

      if (response.data && response.data.status) {
        const contentTypeData = response.data.data[0] || response.data.data;
        if (contentTypeData && contentTypeData.ct_name) {
          form.setFieldsValue({
            ct_name: contentTypeData.ct_name,
            ct_is_active: contentTypeData.ct_is_active || "Y",
          });
          if (Array.isArray(contentTypeData.multilingual_data)) {
            setMultilingualData(
              contentTypeData.multilingual_data.map((d) => ({
                lang_id: d.lang_id ?? null,
                ct_name: d.ct_name ?? "",
                seo_description: d.seo_description ?? "",
                seo_keywords: d.seo_keywords ?? "",
              }))
            );
          }
        } else {
          notify.error({ message: "Content type data not found" });
          navigate("/content-types");
        }
      } else {
        notify.error({
          message:
            response.data?.message || "Failed to fetch content type details",
        });
        navigate("/content-types");
      }
    } catch (error) {
      console.error("Error fetching content type details:", error);
      notify.error({ message: "Error fetching content type details" });
      navigate("/content-types");
    } finally {
      setLoadingData(false);
    }
  }, [ct_id, form, navigate]);

  useEffect(() => {
    setPageTitle("Edit Content Type");
    fetchContentTypeDetails();
  }, [fetchContentTypeDetails, setPageTitle]);

  // Load languages
  useEffect(() => {
    const loadLanguages = async () => {
      try {
        const params = {
          perPage: 1000,
          currentPage: 1,
          searchText: "",
          status: "Y",
        };
        const res = await masterService.getVideoLanguages(params);
        const list =
          res?.data?.data?.data || res?.data?.data || res?.data || [];
        setLanguages(list);
      } catch (e) {
        notify.error({ message: "Failed to load languages" });
      }
    };
    loadLanguages();
  }, []);

  const handleSubmit = async (values) => {
    // Validate multilingual data
    const langs = multilingualData.map((d) => d.lang_id).filter(Boolean);
    if (new Set(langs).size !== langs.length) {
      notify.error({ message: "Duplicate languages in multilingual data" });
      return;
    }
    for (let i = 0; i < multilingualData.length; i++) {
      const d = multilingualData[i];
      if (d.lang_id && !d.ct_name) {
        notify.error({
          message: `Language entry #${
            i + 1
          }: Content type name is required when language is selected`,
        });
        return;
      }
    }
    setLoading(true);
    try {
      const multilingualSerialized = (multilingualData || []).map((d) => ({
        ...d,
        seo_keywords: Array.isArray(d.seo_keywords)
          ? d.seo_keywords.join(",")
          : d.seo_keywords || "",
      }));
      const parentKeywords = Array.isArray(values.seo_keywords)
        ? values.seo_keywords
            .map((s) => String(s).trim())
            .filter(Boolean)
            .join(",")
        : values.seo_keywords || "";
      const payload = {
        ...values,
        ct_id: ct_id,
        seo_keywords: parentKeywords,
        multilingual_data: multilingualSerialized,
      };

      const response = await masterService.updateContentType(ct_id, payload);

      if (response.data && response.data.status) {
        notify.success({
          message: response.data.message || "Content type updated successfully",
        });
        navigate("/content-types");
      } else {
        notify.error({
          message: response.data.message || "Failed to update content type",
        });
      }
    } catch (error) {
      console.error("Error updating content type:", error);
      notify.error({ message: "Error updating content type" });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    form.resetFields();
  };

  const handleGoBack = () => {
    navigate("/content-types");
  };

  return (
    <div className="min-h-screen">
      <Spin spinning={loadingData}>
        <Card
        >
          <Form
            form={form}
            layout="vertical"
            size="large"
            onFinish={handleSubmit}
            className="space-y-6"
          >
            {/* Basic Information Section */}
            <div
              className="bg-white p-6 rounded-lg border border-gray-200"
              style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9" }}
            >
              <div className="flex items-center gap-2 mb-4">
                <FileTextOutlined />
                <Title
                  level={4}
                  className="!mb-0"
                  style={{ color: "rgba(0, 0, 0, 0.88)" }}
                >
                  Content Type Information
                </Title>
              </div>

              <Row gutter={[24, 16]}>
                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    label={
                      <span
                        className="font-semibold"
                        style={{ color: "rgba(0, 0, 0, 0.88)" }}
                      >
                        Content Type
                      </span>
                    }
                    name="ct_name"
                    rules={[
                      { required: true, message: "Please enter content type" },
                      { validator: noWhitespaceValidator },
                    ]}
                  >
                    <Input
                      placeholder="Enter content type"
                      className="rounded-lg"
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    label={
                      <span
                        className="font-semibold"
                        style={{ color: "rgba(0, 0, 0, 0.88)" }}
                      >
                        Status
                      </span>
                    }
                    name="ct_is_active"
                  >
                    <Select className="rounded-lg">
                      <Option value="Y">
                        <Badge status="success" text="Active" />
                      </Option>
                      <Option value="N">
                        <Badge status="default" text="Inactive" />
                      </Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                label={
                  <span
                    className="font-semibold"
                    style={{ color: "rgba(0, 0, 0, 0.88)" }}
                  >
                    Keywords
                  </span>
                }
                name="seo_keywords"
              >
                <Select
                  mode="tags"
                  placeholder="Type a keyword and press Enter"
                  tokenSeparators={[","]}
                  open={false}
                  className="rounded-lg"
                />
              </Form.Item>

              <Form.Item
                label={
                  <span
                    className="font-semibold"
                    style={{ color: "rgba(0, 0, 0, 0.88)" }}
                  >
                    Description
                  </span>
                }
                name="seo_description"
                rules={[{ validator: noWhitespaceValidator }]}
              >
                <Input.TextArea
                  rows={4}
                  placeholder="Enter content type description"
                  className="rounded-lg"
                  showCount
                  maxLength={500}
                />
              </Form.Item>
            </div>

            {/* Multilingual Data Section */}
            <div
              className="bg-white p-6 rounded-lg border border-gray-200"
              style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9" }}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <GlobalOutlined />
                  <Title
                    level={4}
                    className="!mb-0"
                    style={{ color: "rgba(0, 0, 0, 0.88)" }}
                  >
                    Multilingual Data
                  </Title>
                </div>
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={() =>
                    setMultilingualData([
                      ...multilingualData,
                      {
                        lang_id: null,
                        ct_name: "",
                        seo_description: "",
                        seo_keywords: [],
                      },
                    ])
                  }
                  className="rounded-lg"
                >
                  Add Language
                </Button>
              </div>

              {multilingualData.length === 0 && (
                <div
                  className="text-center p-6 rounded-lg"
                  style={{
                    background: "#fafafa",
                    border: "1px dashed #d9d9d9",
                  }}
                >
                  <Text type="secondary" className="text-base">
                    No multilingual data added yet. Click "Add Language" to get
                    started.
                  </Text>
                </div>
              )}

              <div className="space-y-4">
                {multilingualData.map((data, index) => (
                  <Card
                    key={`ml-${index}`}
                    className="rounded-lg"
                    title={
                      <div className="flex items-center gap-2">
                        <Text strong className="text-base">
                          Language Entry #{index + 1}
                        </Text>
                        {data.lang_id && (
                          <Badge
                            count={
                              languages.find((l) => l.lang_id === data.lang_id)
                                ?.lang_name || "Unknown Language"
                            }
                            style={{ backgroundColor: "#1890ff" }}
                          />
                        )}
                      </div>
                    }
                    extra={
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() =>
                          setMultilingualData(
                            multilingualData.filter((_, i) => i !== index)
                          )
                        }
                      >
                        Remove
                      </Button>
                    }
                  >
                    <Row gutter={[16, 16]}>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          label={
                            <span
                              className="font-semibold"
                              style={{ color: "rgba(0, 0, 0, 0.88)" }}
                            >
                              Language
                            </span>
                          }
                          required
                          style={{ marginBottom: 0 }}
                        >
                          <Select
                            value={data.lang_id}
                            placeholder="Select language"
                            onChange={(value) => {
                              const arr = [...multilingualData];
                              arr[index].lang_id = value;
                              setMultilingualData(arr);
                            }}
                            showSearch
                            className="rounded-lg"
                            filterOption={(input, option) =>
                              option.children
                                .toLowerCase()
                                .indexOf(input.toLowerCase()) >= 0
                            }
                          >
                            {languages.map((lang) => (
                              <Option key={lang.lang_id} value={lang.lang_id}>
                                {lang.lang_name}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>

                      <Col xs={24} sm={12}>
                        <Form.Item
                          label={
                            <span
                              className="font-semibold"
                              style={{ color: "rgba(0, 0, 0, 0.88)" }}
                            >
                              Content Type
                            </span>
                          }
                          required
                          style={{ marginBottom: 0 }}
                          rules={[{ validator: noWhitespaceValidator }]}
                        >
                          <Input
                            value={data.ct_name}
                            placeholder="Enter content type in this language"
                            className="rounded-lg"
                            onChange={(e) => {
                              const arr = [...multilingualData];
                              arr[index].ct_name = e.target.value;
                              setMultilingualData(arr);
                            }}
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={[16, 16]} className="mt-4">
                      <Col xs={24} sm={12}>
                        <Form.Item
                          label={
                            <span
                              className="font-semibold"
                              style={{ color: "rgba(0, 0, 0, 0.88)" }}
                            >
                              SEO Keywords
                            </span>
                          }
                          style={{ marginBottom: 0 }}
                        >
                          <Select
                            mode="tags"
                            value={
                              Array.isArray(data.seo_keywords)
                                ? data.seo_keywords
                                : data.seo_keywords
                                ? String(data.seo_keywords)
                                    .split(",")
                                    .map((s) => s.trim())
                                    .filter(Boolean)
                                : []
                            }
                            placeholder="Type a keyword and press Enter"
                            onChange={(value) => {
                              const arr = [...multilingualData];
                              arr[index].seo_keywords = value;
                              setMultilingualData(arr);
                            }}
                            tokenSeparators={[","]}
                            open={false}
                            className="rounded-lg"
                          />
                        </Form.Item>
                      </Col>

                      <Col xs={24} sm={12}>
                        <Form.Item
                          label={
                            <span
                              className="font-semibold"
                              style={{ color: "rgba(0, 0, 0, 0.88)" }}
                            >
                              SEO Description
                            </span>
                          }
                          style={{ marginBottom: 0 }}
                          rules={[{ validator: noWhitespaceValidator }]}
                        >
                          <Input.TextArea
                            rows={3}
                            value={data.seo_description}
                            placeholder="Enter SEO description"
                            className="rounded-lg"
                            maxLength={256}
                            onChange={(e) => {
                              const arr = [...multilingualData];
                              arr[index].seo_description = e.target.value;
                              setMultilingualData(arr);
                            }}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Card>
                ))}
              </div>
            </div>

            {/* Action Buttons */}
            <div
              className="flex justify-end gap-4 pt-6 border-t"
              style={{ borderColor: "#d9d9d9" }}
            >
              <Space size="middle">
                <Button
                  onClick={handleReset}
                  icon={<ReloadOutlined />}
                  size="large"
                  className="px-6"
                >
                  Reset
                </Button>
                <Button
                  onClick={handleGoBack}
                  icon={<ArrowLeftOutlined />}
                  size="large"
                  className="px-6"
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  icon={<SaveOutlined />}
                  size="large"
                  className="px-8"
                >
                  {loading ? "Updating..." : "Update Content Type"}
                </Button>
              </Space>
            </div>
          </Form>
        </Card>
      </Spin>
    </div>
  );
};

export default EditContentType;
